<template>
  <div class="app flex-row align-items-center register-page">
    <b-container fluid>
      <!-- Logo -->
      <div class="text-center logo-wrap">
        <img :src="partnerBranding && partnerBranding.logo_url ? partnerBranding.logo_url : '/img/logo.png'" alt="Logo" class="auth-logo">
      </div>

      <!-- Partner badge -->
      <div v-if="partner" class="text-center partner-badge-wrap">
        <div class="partner-badge">
          <span class="partner-badge-label">{{ partnerBranding ? $t('partner.registrationVia') : $t('partner.partnerRegistration') }}</span>
          <span v-if="partnerBranding" class="partner-badge-name">{{ partnerBranding.name }}</span>
        </div>
      </div>

      <b-row class="justify-content-center">
        <b-col cols="12" lg="5" xl="4">
          <div class="register-card">
            <div class="form-panel d-flex flex-column">
              <div class="text-center mb-4">
                <h1 class="form-title">{{ $t('register') }}</h1>
                <p class="text-muted small mb-0">{{ $t('noCardRequired') }}</p>
              </div>

              <b-form v-on:keyup.enter="(e) => register(e)">
                <b-form-group>
                  <InputField name="name" :label="$t('name')" v-model="name" @error="fieldError" :rules="{required:true, length: {min: 2}}" />
                </b-form-group>
                <b-form-group>
                  <InputField name="email" type="email" :label="$t('email')" v-model="email" @error="fieldError" :errors="httpErrors.email" :rules="{required:true, length: {min: 2}}" />
                </b-form-group>
                <b-form-group>
                  <InputField name="project_name" :label="$t('projectName')" v-model="project_name" @error="fieldError" :rules="{length: {min: 2, max: 30}}" />
                </b-form-group>
                <b-form-group>
                  <InputField type="password" name="password" :label="$t('password')" v-model="password" @error="fieldError" :rules="{required:true, length: {min: 2}}" />
                </b-form-group>
                <b-form-group>
                  <InputField type="password" name="confirm_password" :label="$t('confirmPassword')" v-model="confirm_password" @error="fieldError" :errors="extendErrors['confirm_password']" :rules="{required:true, length: {min: 2}}" />
                </b-form-group>

                <!-- Product install notice -->
                <div v-if="hasProduct" class="product-install-notice mt-3">
                  <div v-if="(loadingTemplate && !template) || (loadingIntegration && !integration) || (loadingApp && !app)" class="install-loading">
                    <b-spinner small variant="primary" class="mr-2"></b-spinner>
                    <span class="text-muted small">{{ loadingLabel }}</span>
                  </div>
                  <template v-else-if="productItems.length">
                    <p class="install-label">{{ $t('willBeInstalledUponRegistration') }}</p>
                    <div class="product-grid">
                      <div v-for="(item, idx) in productItems" :key="idx" class="product-chip">
                        <div class="chip-icon">
                          <b-icon v-if="item.iconType === 'bicon'" :icon="item.iconValue" class="chip-bicon"></b-icon>
                          <img v-else-if="item.iconType === 'image'" :src="item.iconValue" :alt="item.label" class="chip-img">
                          <span v-else class="chip-initial">{{ item.label.charAt(0).toUpperCase() }}</span>
                        </div>
                        <span class="chip-label">{{ item.label }}</span>
                      </div>
                    </div>
                  </template>
                </div>

                <!-- Terms of Service -->
                <div v-if="hasProduct" class="tos-checkbox mt-3">
                  <b-form-checkbox v-model="tosAccepted" :class="{ 'is-invalid': tosError }">
                    {{ $t('iAgreeWith') }}
                    <a href="https://protokol.io/assets/TermsOfService_live_since_12102021-ChwEom_s.pdf" target="_blank" rel="noopener noreferrer">{{ $t('termsOfService') }}</a>
                  </b-form-checkbox>
                  <div v-if="tosError" class="tos-error">{{ $t('tosRequired') }}</div>
                </div>

                <b-alert variant="danger" :show="!!registerError" class="mt-3 mb-0">{{ registerError }}</b-alert>

                <b-button variant="success" size="lg" block @click="(e) => register(e)" :disabled="registering || (hasProduct && !tosAccepted)" class="register-btn mt-4 mb-3">
                  <b-spinner v-if="registering" small class="mr-2"></b-spinner>
                  {{ $t('register') }}
                </b-button>

                <p class="text-center text-muted small mb-0">
                  {{ $t('alreadyHaveAccount') }} <router-link to="/login">{{ $t('signIn') }}</router-link>
                </p>
              </b-form>
            </div>
          </div>
        </b-col>
      </b-row>
    </b-container>

    <!-- Confirmation Email Dialog -->
    <main-dialog :show="showConfirmationDialog" title="" @close="$router.push({ path: '/login', query: { email: email } })" max-width="480px">
      <div class="confirmation-dialog-body">
        <div class="confirmation-icon-wrap">
          <div class="confirmation-icon">
            <i class="bi bi-envelope-check"></i>
          </div>
        </div>
        <h3 class="confirmation-title">{{ $t('confirmationEmailTitle') }}</h3>
        <p class="confirmation-subtitle">{{ $t('confirmationEmailSubtitle', { email: email }) }}</p>
        <b-button variant="success" block size="lg" class="confirmation-btn" @click="$router.push({ path: '/login', query: { email: email } })">
          {{ $t('login') }}
        </b-button>
      </div>
    </main-dialog>

    <!-- Video Modal -->
    <main-dialog :show="showVideoModal" :title="$t('watchVideo')" @close="showVideoModal = false" max-width="900px">
      <div class="video-container">
        <video v-if="template && template.template.video" :src="template.template.video" controls autoplay class="w-100">
          {{ $t('videoNotSupported') }}
        </video>
      </div>
    </main-dialog>
  </div>
</template>

<script>
import IdentityFields from "@/components/identityFields/IdentityFields"
import Input from "@/components/form/Input"
import Select from '@/components/form/Select'
import { Switch as cSwitch } from '@coreui/vue'
import PaymentInfo from '../projects/Settings/PaymentInfo.vue'
import Template from "../../lib/Template"
import publicHttp from "../../shared/publicHttp"
import Description from "../templates/Description"
import MainDialog from "@/components/MainDialog"

export default {
  components: {
    IdentityFields,
    InputField: Input,
    SelectField: Select,
    cSwitch,
    PaymentInfo,
    Description,
    MainDialog
  },
  name: 'Register',
  data: function() {
    return {
      appMode: this.$env.get('MODE'),
      name: "",
      email: "",
      project_name: "My Company",
      password: "",
      ref: "",
      confirm_password: "",
      errors: [],
      registering: false,
      httpErrors: {},
      extendErrors: {},
      template: null,
      loadingTemplate: false,
      showVideoModal: false,
      showConfirmationDialog: false,
      integration: null,
      loadingIntegration: false,
      app: null,
      loadingApp: false,
      account: null,
      accountPlan: null,
      partner: null,
      partnerBranding: null,
      partnerAccounts: [],
      registerError: null,
      tosAccepted: false,
      tosError: false,
      fields: {
        name: {
          key: 'name',
          type: "text",
          module: "input",
          name: this.$t('name'),
          constraints: {
              required: true,
              length: {
                  min: 3
              },
              not_allowed_words: ""
          }
        },
        email: {
          key: 'email',
          type: "email",
          module: "input",
          name: this.$t('email'),
          constraints: {
              required: true,
              not_allowed_words: ""
          }
        },
        project: {
          key: 'project',
          type: "text",
          module: "input",
          name: this.$t('projectName'),
          constraints: {
              length: {
                  min: 2,
                  max: 55
              },
              not_allowed_words: ""
          }
        },
        password: {
          key: 'password',
          type: "password",
          module: "input",
          name: this.$t('password'),
          constraints: {
              required: true,
              length: {
                  min: 7,
                  max: 50
              },
          }
        },
        confirmPassword: {
          key: 'confirm_password',
          type: "password",
          module: "input",
          name: this.$t('confirmPassword'),
          constraints: {
              required: true,
              length: {
                  min: 7,
                  max: 50
              },
          }
        }
      }
    }
  },
  methods: {
    fieldError(error) {
      let found = this.errors.find((i, e) => {
          return i.field == error.field
      })

      if (found === undefined) {
          this.errors.push(error)
      } else {
          this.errors.map(e => {
              if (found.field == e.field)
                  e.error = error.error
          })
      }
     },

    resolveIcon(raw) {
      if (!raw) return { iconType: 'initial', iconValue: null }
      if (raw.indexOf('b-icon:') === 0) return { iconType: 'bicon', iconValue: raw.slice(7) }
      if (raw.indexOf('image:') === 0) return { iconType: 'image', iconValue: raw.slice(6) }
      return { iconType: 'image', iconValue: raw }
    },

    translate(label) {
      try {
        return label.locales[this.locale]
      } catch {
        return false
      }
    },

  checkForErrors() {
      return this.errors.find((i, e) => {
          return i.error
      })
  },

    register(e) {
      e.preventDefault()
      this.registering = true
      this.registerError = null
      this.httpErrors = {}

      if (this.hasProduct && !this.tosAccepted) {
        this.tosError = true
        this.registering = false
        return
      }

      if (this.checkForErrors()) {
        this.registering = false
        this.registerError = this.$t('fieldsNotValid')
        return
      }

      let products = []
      const productOption = this.$route.query.option || ''
      if (this.template) {
        products.push({
          product_type: "template",
          reference: this.template.template.id,
          source: "template",
          options: { variant: productOption }
        })
      }
      if (this.integration) {
        products.push({
          product_type: "integration",
          reference: this.integration.id,
          source: "marketplace",
          options: { variant: productOption }
        })
      }
      if (this.app) {
        products.push({
          product_type: "app",
          reference: this.app.uuid,
          source: "marketplace",
          options: { variant: productOption }
        })
      }

      this.$http.post("/v1/user/register_legacy", {
        name: this.name,
        email: this.email,
        password: this.password,
        project_name: this.project_name,
        ref: this.ref,
        partner: this.partner || '',
        products: products,
        lang: this.$i18n.locale,
        account_type: this.account || '',
        account_option: this.$route.query.option || ''
      }).then(resp => {
        let project = resp.data

        this.$gtag.event('register_completed', {project: project})

        if (resp.status == 100) {
          this.$bvToast.toast(this.$t('failedToSetupTrialOnRegister'), {
            type: 'warning'
          })
        }

        this.registering = false
        this.showConfirmationDialog = true
      }).catch(error => {
        this.registering = false

        if (error.response !== undefined) {
          if (error.response.status == 417) {
            let errors = error.response.data.errors || {}
            if (errors['email'] !== undefined) {
              this.step = 'info'
            }
            this.httpErrors = errors
            this.registerError = this.$t('fieldsNotValid')
          } else {
            this.registerError = (error.response.data && error.response.data.message) || this.$t('unexpectedError')
          }
        } else {
          this.registerError = this.$t('unexpectedError')
        }

        console.error("Error registering user", error)
      })
    },

  },

  computed: {
    locale() {
      if (this.$route.query.lang) {
        return this.$route.query.lang
      }

      return this.$store.getters.getLocale
    },

    activationCode() {
      return this.$route.query.activation_code
    },

    price() {
      if (!this.template) return 0
      return new Template(this.template).priceInCurrency(this.currency)
    },

    currency() {
      return this.$route.query.currency || "usd"
    },

    hasProduct() {
      return !!(this.template || this.loadingTemplate || this.integration || this.loadingIntegration || this.app || this.loadingApp || this.account)
    },

    loadingLabel() {
      if (this.loadingTemplate) return this.$t('loadingTemplate')
      if (this.loadingIntegration) return this.$t('loadingIntegration')
      return this.$t('loadingApp')
    },

    productItems() {
      const self = this
      const items = []
      function item(label, rawIcon) {
        const resolved = self.resolveIcon(rawIcon)
        return { label: label, iconType: resolved.iconType, iconValue: resolved.iconValue }
      }
      if (this.account) {
        const label = this.accountPlan ? this.accountPlan.name : this.account
        items.push(item(label, null))
      }
      if (this.template) {
        const t = this.template.template
        if (t.integrations && t.integrations.length) {
          t.integrations.forEach(i => items.push(item(self.$translateLabel(i.label), i.icon || null)))
        }
        if (t.apps && t.apps.length) {
          t.apps.forEach(a => items.push(item(self.$translateLabel(a.label), a.icon || null)))
        }
        if (t.components && t.components.length) {
          t.components.forEach(c => items.push(item(self.$translateLabel(c.label), c.icon || null)))
        }
        if (items.length === (this.account ? 1 : 0)) {
          items.push(item(self.$translateLabel(t.label), t.cover || null))
        }
        return items
      }
      if (this.integration) {
        items.push(item(this.$translateLabel(this.integration.label), this.integration.icon || null))
        return items
      }
      if (this.app) {
        items.push(item(this.$translateLabel(this.app.label), this.app.icon || null))
        return items
      }
      return items
    }
  },

  watch: {
    "confirm_password": function(n, o) {
       if (n != this.password) {
         this.extendErrors["confirm_password"] = ["password_not_match"]
       } else {
          this.$delete(this.extendErrors, 'confirm_password')
       }
    },

    "password": function(n, o) {
      if (n == this.password) {
          this.$delete(this.extendErrors, 'confirm_password')
      }
    },

    "email": function(n, o) {
      this.$delete(this.httpErrors, 'email')
    },

    "tosAccepted": function(n) {
      if (n) this.tosError = false
    }
  },

  mounted() {
    this.ref = this.$route.query.ref

    if (this.$route.query.partner) {
      this.partner = this.$route.query.partner
      publicHttp.get("/v1/user/register/partner/" + this.partner + "/accounts").then((resp) => {
        this.partnerAccounts = resp.data.partner_accounts || []
        var allAccounts = this.partnerAccounts.concat(resp.data.standard_accounts || [])
        if (allAccounts.length === 1) {
          this.account = allAccounts[0].type || allAccounts[0].slug
        }
        if (resp.data.branding) {
          this.partnerBranding = resp.data.branding
        }
      }).catch(err => {
        console.error("Failed to load partner info:", err)
      })
    }

    if (this.$route.query.template) {
      this.loadingTemplate = true
      publicHttp.get("/luma/public/marketplace/product/template/" + this.$route.query.template).then((resp) => {
        this.template = new Template(
            resp.data,
            this.locale,
            this.activationCode
        )
        this.loadingTemplate = false
        console.log("Template loaded:", this.template)
      }).catch(err => {
        console.error("Failed to load template:", err)
        this.loadingTemplate = false
      })
    }

    if (this.$route.query.integration) {
      this.loadingIntegration = true
      publicHttp.get("/luma/public/marketplace/product/integration/" + this.$route.query.integration).then((resp) => {
        this.integration = resp.data
        this.loadingIntegration = false
      }).catch(err => {
        console.error("Failed to load integration:", err)
        this.loadingIntegration = false
      })
    }

    if (this.$route.query.app) {
      this.loadingApp = true
      publicHttp.get("/luma/public/marketplace/product/app/" + this.$route.query.app).then((resp) => {
        this.app = resp.data
        this.loadingApp = false
      }).catch(err => {
        console.error("Failed to load app:", err)
        this.loadingApp = false
      })
    }

    if (this.$route.query.plan) {
      this.account = this.$route.query.plan
      publicHttp.get("/luma/public/marketplace/product/account/" + this.$route.query.plan).then((resp) => {
        this.accountPlan = resp.data
      }).catch(() => {
        // fallback: account remains set, name falls back to raw type
      })
    }

  }
}
</script>

<style scoped lang="scss">
.auth-logo {
  max-width: 52px;
  height: auto;
}

.logo-wrap {
  padding: 2rem 0 1.5rem;
}

.partner-badge-wrap {
  margin-bottom: 1rem;
}

.partner-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.35rem 0.9rem;
  background: rgba(102, 126, 234, 0.08);
  border: 1px solid rgba(102, 126, 234, 0.2);
  border-radius: 20px;
  font-size: 0.8rem;
}

.partner-badge-label {
  color: var(--text-muted);
}

.partner-badge-name {
  font-weight: 600;
  color: var(--text-primary);
}

.register-page {
  min-height: 100vh;
  display: flex;
  align-items: flex-start;
  padding-bottom: 3rem;
}

.register-card {
  background: var(--bg-card);
  border: 1px solid var(--border-default);
  border-radius: var(--radius-2xl);
  box-shadow: var(--shadow-md);
  overflow: hidden;
}

// ─── Product install notice ───────────────────────────────────────────────────

.product-install-notice {
  border: 1px solid var(--border-default);
  border-radius: var(--radius-lg);
  padding: 1rem 1.1rem;
  background: rgba(102, 126, 234, 0.04);
}

.install-loading {
  display: flex;
  align-items: center;
}

.install-label {
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--text-muted);
  margin-bottom: 0.6rem;
}

.product-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 0.45rem;
}

.product-chip {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.3rem 0.65rem 0.3rem 0.3rem;
  background: var(--bg-card);
  border: 1px solid var(--border-default);
  border-radius: 20px;
  font-size: 0.82rem;
  color: var(--text-secondary);
}

.chip-icon {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, var(--primary-start), var(--primary-end));
  flex-shrink: 0;
}

.chip-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.chip-bicon {
  font-size: 0.7rem;
  color: #fff;
}

.chip-initial {
  font-size: 0.7rem;
  font-weight: 700;
  color: #fff;
  line-height: 1;
}

.chip-label {
  font-weight: 500;
  white-space: nowrap;
}

// ─── Terms of Service ─────────────────────────────────────────────────────────

.tos-checkbox {
  font-size: 0.85rem;
  color: var(--text-secondary);

  a {
    color: var(--primary-start, #667eea);
    text-decoration: underline;
    font-weight: 500;

    &:hover {
      opacity: 0.85;
    }
  }

  &.is-invalid,
  .is-invalid {
    color: var(--danger, #dc3545);
  }
}

.tos-error {
  font-size: 0.78rem;
  color: var(--danger, #dc3545);
  margin-top: 0.25rem;
  padding-left: 1.5rem;
}

// ─── Form panel ────────────────────────────────────────────────────────────────

.form-panel {
  padding: 2rem 2rem 1.75rem;
}

.form-title {
  font-size: 1.4rem;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 0.25rem;
}

.register-btn {
  background: linear-gradient(135deg, var(--primary-start), var(--primary-end));
  border: none;
  font-weight: 600;
  letter-spacing: 0.02em;
  padding: 0.75rem;
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-primary);
  transition: var(--transition-default);

  &:hover:not(:disabled) {
    box-shadow: var(--shadow-primary-hover);
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.7;
  }
}

// ─── Video ──────────────────────────────────────────────────────────────────────

.video-container {
  video {
    width: 100%;
    border-radius: var(--radius-md);
  }
}

// ─── Confirmation Dialog ─────────────────────────────────────────────────────────

.confirmation-dialog-body {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 0.5rem 1rem 1.25rem;
}

.confirmation-icon-wrap {
  margin-bottom: 1.5rem;
}

.confirmation-icon {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: linear-gradient(135deg, #d1fae5, #a7f3d0);
  display: flex;
  align-items: center;
  justify-content: center;

  i {
    font-size: 2rem;
    color: #059669;
  }
}

.confirmation-title {
  font-size: 1.35rem;
  font-weight: 700;
  margin-bottom: 0.75rem;
  color: var(--text-primary, #111827);
}

.confirmation-subtitle {
  font-size: 0.9rem;
  color: var(--text-muted, #6b7280);
  line-height: 1.6;
  margin-bottom: 1.75rem;
  max-width: 340px;
}

.confirmation-btn {
  background: linear-gradient(135deg, var(--primary-start), var(--primary-end));
  border: none;
  font-weight: 600;
  letter-spacing: 0.02em;
  padding: 0.75rem;
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-primary);
  transition: var(--transition-default);
  width: 100%;

  &:hover {
    box-shadow: var(--shadow-primary-hover);
    transform: translateY(-1px);
  }
}

// ─── Responsive ─────────────────────────────────────────────────────────────────

@media (max-width: 767px) {
  .register-page {
    padding-bottom: 2rem;
  }

  .logo-wrap {
    padding: 1rem 0 1rem;
  }
}
</style>

